import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../styles/theme';
import { FaCalendarCheck, FaCreditCard, FaLink, FaChevronLeft, FaChevronRight, FaCrown, FaCog, FaPlug } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';

interface NavigationSidebarProps {
  currentView: 'bookings' | 'payments' | 'trackable-links' | 'webhooks';
  onNavigate: (view: 'bookings' | 'payments' | 'trackable-links' | 'webhooks') => void;
  planTier?: string;
}

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({ currentView, onNavigate, planTier = 'free' }) => {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const [manuallyCollapsed, setManuallyCollapsed] = useState(false);
  const isCollapsed = isMobile || manuallyCollapsed;
  const navigate = useNavigate();

  const menuItems: Array<{
    id: 'bookings' | 'payments' | 'trackable-links' | 'webhooks';
    label: string;
    icon: React.ComponentType<IconBaseProps>;
  }> = [
    { id: 'bookings' as const, label: 'Trackable Bookings', icon: FaCalendarCheck as React.ComponentType<IconBaseProps> },
    { id: 'payments' as const, label: 'Payments', icon: FaCreditCard as React.ComponentType<IconBaseProps> },
    { id: 'trackable-links' as const, label: 'Trackable Links', icon: FaLink as React.ComponentType<IconBaseProps> },
    { id: 'webhooks' as const, label: 'Webhooks', icon: FaPlug as React.ComponentType<IconBaseProps> }
  ];

  const sidebarWidth = isCollapsed ? '60px' : '250px';

  return (
    <div style={{
      position: 'fixed',
      left: 0,
      top: '80px', // Below header
      height: 'calc(100vh - 80px)',
      width: sidebarWidth,
      backgroundColor: theme.colors.surface,
      borderRight: `1px solid ${theme.colors.border}`,
      padding: isCollapsed ? '12px 0' : '24px 0',
      zIndex: 100,
      overflowY: 'auto',
      transition: 'width 0.3s ease'
    }}>
      {/* Toggle Button — hidden on mobile (always icon-only there) */}
      {!isMobile && <button
        onClick={() => setManuallyCollapsed(!manuallyCollapsed)}
        style={{
          position: 'absolute',
          top: '12px',
          right: '-12px',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: theme.colors.primary,
          color: '#FFFFFF',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: theme.shadows.md,
          zIndex: 101,
          transition: 'all 0.2s ease'
        }}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? FaChevronRight({ size: 12 }) : FaChevronLeft({ size: 12 })}
      </button>}

      <nav style={{ marginTop: '20px' }}>
        {menuItems.map((item) => {
          const isActive = currentView === item.id;
          const Icon = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              style={{
                width: '100%',
                padding: isCollapsed ? '16px' : '16px 24px',
                border: 'none',
                backgroundColor: isActive ? theme.colors.primary : 'transparent',
                color: isActive ? '#FFFFFF' : theme.colors.text.primary,
                fontSize: theme.typography.sizes.base,
                fontWeight: isActive ? theme.typography.weights.semibold : theme.typography.weights.medium,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: '12px',
                borderLeft: isActive ? `4px solid ${theme.colors.accent}` : '4px solid transparent',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = theme.colors.background;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              title={isCollapsed ? item.label : undefined}
            >
              <Icon size={20} />
              {!isCollapsed && <span>{item.label}</span>}
            </button>
          );
        })}

        {/* Divider */}
        <div style={{
          height: '1px',
          backgroundColor: theme.colors.border,
          margin: '16px 12px'
        }} />

        {/* Upgrade to Pro (only for free tier) */}
        {planTier === 'free' && (
          <button
            onClick={() => navigate('/pricing')}
            style={{
              width: '100%',
              padding: isCollapsed ? '16px' : '16px 24px',
              border: 'none',
              backgroundColor: 'transparent',
              color: theme.colors.primary,
              fontSize: theme.typography.sizes.base,
              fontWeight: theme.typography.weights.semibold,
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              gap: '12px',
              borderLeft: '4px solid transparent',
              whiteSpace: 'nowrap',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            title={isCollapsed ? 'Upgrade to Pro' : undefined}
          >
            {FaCrown({ size: 20 })}
            {!isCollapsed && <span>Upgrade to Pro</span>}
          </button>
        )}

        {/* Subscription Management */}
        <button
          onClick={() => navigate('/subscription')}
          style={{
            width: '100%',
            padding: isCollapsed ? '16px' : '16px 24px',
            border: 'none',
            backgroundColor: 'transparent',
            color: theme.colors.text.primary,
            fontSize: theme.typography.sizes.base,
            fontWeight: theme.typography.weights.medium,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            gap: '12px',
            borderLeft: '4px solid transparent',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = theme.colors.background;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title={isCollapsed ? 'Subscription' : undefined}
        >
          {FaCog({ size: 20 })}
          {!isCollapsed && <span>Subscription</span>}
        </button>
      </nav>
    </div>
  );
};

export default NavigationSidebar;
